<template>
  <div class="bridge-signing-table">
    <v-card-title class="toolbar primary white--text">
      <div>
        <div class="headline">
          <h1 class="headline ml-2">
            <v-icon dark>
              description
            </v-icon>
            Tax Traits
          </h1>
        </div>
      </div>
      <v-spacer />
      <v-text-field
        v-model="search"
        append-icon="search"
        label="Filter results"
        single-line
        flat
        dark
        color="primary"
        solo-inverted
        hide-details
      />
      <v-tooltip
        top
        dark
      >
        <template v-slot:activator="{ on }">
          <v-btn
            color="secondary"
            fab
            dark
            absolute
            bottom
            right
            @click.stop="onCreate"
            v-on="on"
          >
            <v-icon>note_add</v-icon>
          </v-btn>
        </template>
        <span>Create new tax trait</span>
      </v-tooltip>

      <v-tooltip
        top
        dark
      >
        <template v-slot:activator="{ on }">
          <v-btn
            class="right-margin-double"
            color="secondary"
            fab
            dark
            absolute
            bottom
            right
            @click.stop="() => downloadCsv()"
            v-on="on"
          >
            <v-icon>file_download</v-icon>
          </v-btn>
        </template>
        <span>Download CSV</span>
      </v-tooltip>

      <v-tooltip
        top
        dark
      >
        <template v-slot:activator="{ on }">
          <v-btn
            class="right-margin"
            color="secondary"
            fab
            dark
            absolute
            bottom
            right
            @click.stop="showDialog"
            v-on="on"
          >
            <v-icon>file_upload</v-icon>
          </v-btn>
        </template>
        <span>Upload CSV</span>
      </v-tooltip>

      <v-tooltip
        top
        dark
      >
        <template v-slot:activator="{ on }">
          <v-btn
            color="secondary"
            fab
            dark
            absolute
            bottom
            left
            @click.stop="onRefresh"
            v-on="on"
          >
            <v-icon>refresh</v-icon>
          </v-btn>
        </template>
        <span>Refresh</span>
      </v-tooltip>
    </v-card-title>

    <v-data-table
      :headers="headers"
      :items="filteredItems"
      :pagination.sync="pagination"
      :loading="isLoading"
      loading-text="Loading..."
      disable-initial-sort
    >
      <template
        slot="items"
        slot-scope="props"
      >
        <tr>
          <td class="text-center">
            <v-btn
              style="text-transform: none;"
              outline
              round
              color="grey"
              :href="`/#/users/${props.item.user_id}`"
            >
              <v-icon>
                assignment_ind
              </v-icon>
              {{ props.item.user_id }}
            </v-btn>
          </td>
          <td class="text-center">
            <v-icon
              v-if="props.item.is_legal_entity === true"
              color="blue"
            >
              account_balance
            </v-icon>
            <v-icon
              v-if="props.item.is_legal_entity === false"
              color="green"
            >
              account_box
            </v-icon>
          </td>
          <td class="text-center">
            <v-chip
              v-if="props.item.taxable_in"
              :color="'blue'"
              outline
            >
              {{ props.item.taxable_in }}
            </v-chip>
          </td>
          <td class="text-center">
            {{ props.item.tin }}
          </td>
          <td class="text-center">
            {{ props.item.church_tax_rate }}
          </td>
          <td class="text-center">
            {{ props.item.church_denomination }}
          </td>
          <td class="text-center">
            {{ props.item.church_religion_key }}
          </td>
          <td class="text-center">
            <div v-if="props.item.documents">
              <v-chip
                v-for="document in props.item.documents"
                :key="document.id"
                :color="'blue'"
                outline
                @click.stop="() => onEditDocument(props.item.user_id, document)"
              >
                {{ formatDocumentType(document.type) }}
              </v-chip>
            </div>
            <v-chip
              :color="'blue'"
              outline
              @click.stop="() => onCreateDocument(props.item.user_id)"
            >
              <v-icon>add</v-icon>
            </v-chip>
          </td>
          <td class="text-center">
            <v-tooltip
              top
              dark
            >
              <template v-slot:activator="{ on }">
                <v-btn
                  dark
                  flat
                  icon
                  color="secondary"
                  v-on="on"
                  @click.stop="() => onEdit(props.item)"
                >
                  <v-icon>edit</v-icon>
                </v-btn>
              </template>
              <span>Edit</span>
            </v-tooltip>
            <v-tooltip
              top
              dark
            >
              <template v-slot:activator="{ on }">
                <v-btn
                  dark
                  flat
                  icon
                  color="error"
                  v-on="on"
                  @click.stop="() => showDeleteDialog(props.item.user_id)"
                >
                  <v-icon>delete</v-icon>
                </v-btn>
              </template>
              <span>Delete</span>
            </v-tooltip>
          </td>
        </tr>
      </template>
    </v-data-table>

    <CreateDialog
      v-if="isCreating"
      :is-open="isCreating"
      :tax-trait="currentTaxTrait"
      :toggle-open="onCreate"
    />

    <UploadCsvDialog
      :visible="uploadCSVDialog"
      @close="hideDialog()"
      @fileUpload="(file) => onUploadCsv(file)"
    />

    <DeleteDialog
      :visible="deleteDialog"
      :tax-trait-user-id="deleteDialogId"
      @close="hideDeleteDialog()"
      @submit="onDelete(deleteDialogId)"
    />

    <CreateDocumentDialog
      v-if="isCreatingDocument"
      :is-open="isCreatingDocument"
      :document="currentDocument"
      :tax-trait-id="currentTaxTraitId"
      :toggle-open="onCreateDocument"
    />

    <v-dialog
      v-model="csvUploadingDialog"
      persistent
      width="300"
    >
      <v-card
        color="primary"
        dark
      >
        <v-card-text>
          Please wait while uploading CSV.
          <v-progress-linear
            indeterminate
            color="white"
            class="mb-0"
          />
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="csvResultDialog"
      width="800"
    >
      <v-card
        color="primary"
        dark
        class="pa-3"
      >
        <p class="font-weight-black">
          Uploading CSV finished - Result:
        </p>
        <p>Total in CSV: {{ csvUploadingResult.totalInCsv }}</p>
        <p>Total Valid: {{ csvUploadingResult.totalImported }}</p>
        <p>Total Saved to DB: {{ csvUploadingResult.totalDBInserts }}</p>
        <p>Total Failed: {{ csvUploadingResult.totalFailed }}</p>
        <p
          v-for="item in csvUploadingResult.failedReasons"
          :key="item.index"
          style="background-color: red;"
          class="pa-1"
        >
          {{ item.message }}
        </p>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import CreateDialog from './dialogs/taxTrait/CreateDialog';
import DeleteDialog from './dialogs/taxTrait/DeleteDialog';
import UploadCsvDialog from './dialogs/taxTrait/UploadCsvDialog';
import CreateDocumentDialog from './dialogs/taxTrait/document/CreateDialog';
import { downloadFromS3 } from '@/helpers/axiosS3Downloader';
import { downloadCsvFile } from '@/helpers';

export default {
  name: 'TaxTraits',
  components: {
    CreateDialog,
    DeleteDialog,
    UploadCsvDialog,
    CreateDocumentDialog,
  },
  data() {
    return {
      csvUploadingDialog: false,
      csvUploadingResult: {
        totalDBInserts: 0,
        totalFailed: 0,
        totalImported: 0,
        totalInCsv: 0,
        failedReasons: [],
      },
      csvResultDialog: false,
      currentTaxTrait: {},
      currentDocument: {},
      currentTaxTraitId: 0,
      isLoading: false,
      isCreating: false,
      isCreatingDocument: false,
      uploadCSVDialog: false,
      deleteDialog: false,
      deleteDialogId: '',
      approvalDialogs: {},
      pagination: {
        rowsPerPage: 25,
      },
      search: '',
      headers: [
        { text: 'User ID', value: 'user_id', align: 'center' },
        { text: 'Legal Entity', value: 'is_legal_entity', align: 'center' },
        { text: 'Taxable In', value: 'taxable_in', align: 'center' },
        { text: 'TIN', value: 'tin', align: 'center' },
        { text: 'Church Tax Rate', value: 'church_tax_rate', align: 'center' },
        { text: 'Church Denomination', value: 'church_denomination', align: 'center' },
        { text: 'Church Religion Key', value: 'church_religion_key', align: 'center' },
        { text: 'Documents', value: 'documents', align: 'center' },
        { text: 'Actions', value: null, sortable: false, align: 'center' },
      ],
    };
  },
  computed: {
    documentTypes() {
      const data = this.$store.getters.getPayoutsCreationTypes;
      if (!data || !data.taxTrait || !data.taxTrait.document || !data.taxTrait.document.types) {
        return {};
      }

      return data.taxTrait.document.types;
    },
    taxTraits() {
      return this.$store.getters.getTaxTraitsList;
    },
    filteredItems() {
      const { search } = this;

      return search.length < 1 ?
        this.taxTraits :
        this.taxTraits.filter((item) => {
          let found = false;
          Object.keys(item)
            .map((key) => {
              if (item[key] && String(item[key]).toLowerCase().indexOf(search.toLowerCase()) > -1) {
                found = true;
              }

              return key;
            });

          return found;
        });
    },
  },
  methods: {
    onCreate() {
      this.isCreating = !this.isCreating;
      this.currentTaxTrait = {};
    },
    onEdit(item) {
      this.isCreating = !this.isCreating;
      this.currentTaxTrait = item;
    },
    onCreateDocument(taxTraitId) {
      this.isCreatingDocument = !this.isCreatingDocument;
      this.currentDocument = {};
      this.currentTaxTraitId = taxTraitId;
    },
    onEditDocument(taxTraitId, item) {
      this.isCreatingDocument = !this.isCreatingDocument;
      this.currentDocument = item;
      this.currentTaxTraitId = taxTraitId;
    },
    onRefresh() {
      this.$store.dispatch('fetchTaxTraitAll');
    },
    showDialog() {
      this.uploadCSVDialog = true;
      this.$forceUpdate();
    },
    hideDialog() {
      this.uploadCSVDialog = false;
      this.$forceUpdate();
    },
    onUploadCsv(file) {
      this.csvUploadingDialog = true;
      this.$store
        .dispatch('callTaxTraitImportCSV', file)
        .then((e) => {
          this.csvUploadingDialog = false;
          this.csvUploadingResult = e;
          this.csvResultDialog = true;
          this.$store.dispatch('fetchTaxTraitAll');
        });
    },
    showDeleteDialog(id) {
      this.deleteDialogId = id;
      this.deleteDialog = true;
      this.$forceUpdate();
    },
    hideDeleteDialog() {
      this.deleteDialog = false;
      this.$forceUpdate();
    },
    onDelete(userId) {
      this.$store
        .dispatch('deleteTaxTrait', userId)
        .then(() => {
          this.hideDeleteDialog();
        })
        .catch((err) => {
          this.error = err;
        });
    },
    formatDocumentType(type) {
      return this.documentTypes[type];
    },
    downloadCsv() {
      this.$store
        .dispatch('fetchTaxtraitAllDownloadCsv')
        .then((response) => {
          downloadFromS3(response.data).then((data) => {
            downloadCsvFile(data, response.data.fileName);
          });
        });
    },
  },
};
</script>
<style lang="scss">
.bridge-signing-table {
  > .toolbar {
    position: relative;
    padding: 24px 24px 32px 24px;
    margin-bottom: 32px;
  }
}

.text-center {
  text-align: center;
}

.right-margin {
  margin-right: 70px;
}
.right-margin-double {
  margin-right: 140px;
}
</style>



// WEBPACK FOOTER //
// src/components/payoutsAndTaxes/payout/TaxTraits.vue